import React from 'react';
import { Helmet } from 'react-helmet-async';
import TimerComponent from '../components/TimerComponent';
import { useSingleTimer } from '../hooks/useSingleTimer';
import { useFullscreen } from '../contexts/FullscreenContext';
import { getCanonicalUrl } from '../utils/seoUtils';
import './TimersPage.css';

export default function TimersPage() {
  const timerProps = useSingleTimer();
  const { isFullscreen } = useFullscreen();

  return (
    <div className="page-container">
      <Helmet>
        <title>Countdown Timer Online - Free Timer Clock | Clock Online</title>
        <meta name="description" content="Free online countdown timer with video alarm support. Perfect for Pomodoro technique, cooking, workouts, and time management. Set custom durations easily." />
        <link rel="canonical" href={getCanonicalUrl('/timers')} />
        <meta property="og:title" content="Countdown Timer Online - Free Timer Clock | Clock Online" />
        <meta property="og:description" content="Free online countdown timer with video alarm support. Perfect for Pomodoro technique, cooking, workouts, and time management." />
        <meta property="og:url" content={getCanonicalUrl('/timers')} />
        <meta name="twitter:title" content="Countdown Timer Online - Free Timer Clock | Clock Online" />
        <meta name="twitter:description" content="Free online countdown timer with video alarm support. Perfect for Pomodoro technique and time management." />
      </Helmet>
      {!isFullscreen && (
        <h1 className="text-4xl font-bold text-white text-center mb-8">
          Countdown Timer Online - Free Timer Clock
        </h1>
      )}
      <TimerComponent {...timerProps} />
      
      {!isFullscreen && (
        <div className="mt-16 max-w-4xl mx-auto px-6 text-white">
          <h2 className="text-3xl font-bold text-center mb-8 text-blue-300">
            Complete Guide to Countdown Timer Online
          </h2>
          
          <div className="space-y-8 text-lg leading-relaxed">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ How to Use Countdown Timer Online</h3>
              <p className="mb-4">
                Our Countdown Timer Online is a versatile tool designed to help you track time for any activity. Simply set your desired countdown duration using hours, minutes, and seconds. Use the quick preset buttons for common durations like 5, 10, 15, 30, or 60 minutes, or manually input your custom time. Click "Start Timer" to begin the countdown, and the Countdown Timer Online will alert you when time is up.
              </p>
              <p className="mb-4">
                The Countdown Timer Online features a user-friendly interface that makes time management simple and efficient. Whether you're timing a workout, tracking study sessions, managing cooking times, or organizing your daily activities, the Countdown Timer Online provides accurate and reliable time tracking with clear visual feedback.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Advanced Timer Features</h3>
              <p className="mb-4">
                The Countdown Timer Online includes powerful features to enhance your time management experience. Pause and resume functionality allows you to take breaks without losing your progress. The timer automatically saves your settings, so you can return to your favorite durations quickly. For a more engaging experience, enable the video alarm feature to play YouTube videos when your timer completes.
              </p>
              <p className="mb-4">
                With the Countdown Timer Online, you can customize your alarm experience by choosing from various sound options or linking a YouTube video. The growing volume feature gradually increases the alarm volume, providing a gentle wake-up experience. The timer also displays a visual progress bar showing how much time has elapsed, helping you stay aware of your progress.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500"><span className="text-yellow-400">💡</span> Important Usage Tips</h3>
              <p className="mb-4">
                For the Countdown Timer Online to work properly, keep your browser tab active and do not close the web browser. Do not shut down your personal computer. Turn off Power Save mode on your device to ensure the timer continues running accurately. The Countdown Timer Online requires an active browser tab to function, so make sure to keep it open during your timed activity.
              </p>
              <p className="mb-4">
                The Countdown Timer Online is perfect for productivity techniques like the Pomodoro Technique, where you work for 25 minutes and take a 5-minute break. Use quick presets to set up your work and break intervals efficiently. The timer's automatic save feature remembers your preferences, making it easy to maintain consistent timing routines.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Perfect for Various Activities</h3>
              <p className="mb-4">
                The Countdown Timer Online is ideal for a wide range of activities. Use it for cooking and baking to ensure perfect timing for your recipes. Track exercise intervals and rest periods during workouts. Manage study sessions with focused work periods and breaks. Time presentations and meetings to stay on schedule. The Countdown Timer Online adapts to your needs, making it an essential tool for daily time management.
              </p>
              <p className="mb-4">
                Whether you're a student, professional, athlete, or home chef, the Countdown Timer Online provides the flexibility and reliability you need. The intuitive interface makes it easy to set up timers for any duration, from quick 1-minute reminders to longer multi-hour sessions. Experience the convenience of modern time management with the Countdown Timer Online.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-4 text-cyan-500">▶ Video Alarm Integration</h3>
              <p className="mb-4">
                Enhance your Countdown Timer Online experience with optional video alarm integration. When your timer completes, you can choose to play a YouTube video as your alarm signal. Simply enter a YouTube URL or video ID in the timer settings and enable the video alarm feature. The Countdown Timer Online will automatically play your selected video when the countdown reaches zero.
              </p>
              <p className="mb-4">
                The video alarm feature makes the Countdown Timer Online perfect for wake-up routines, exercise motivation, or custom notification experiences. Choose motivational videos, calming music, or any content that helps you stay engaged and alert. The Countdown Timer Online seamlessly integrates video playback with precise timing, creating a personalized and effective time management solution.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
